import { Inject, Injectable, Signal } from '@angular/core';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { Identity, IdentityService } from 'src/app/core/identity.service';
import { FeatureManagementFlag } from 'src/app/shared/models/feature-management/feature-management-flag';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { filter, finalize, from, map, Observable, ReplaySubject, switchMap, tap } from 'rxjs';
import { LaunchDarklyService } from 'src/app/shared/services/ld.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class LDFeatureManager {
    private readonly _ldClient: LDClient.LDClient;
    private _cachedFlags: LDClient.LDFlagSet;
    private readonly _flags$: ReplaySubject<FlagsState> = new ReplaySubject<FlagsState>(1);
    private _instantiated = false;

    constructor(
        @Inject(APP_CONFIG) readonly settings: Settings,
        private readonly launchDarklyService: LaunchDarklyService,
        private readonly identityService: IdentityService
    ) {
        this._ldClient = this.launchDarklyService.createClient(this.settings.launchDarklyClientSdkKey, {
            key: 'anonymous',
            anonymous: true
        });
    }

    public async init() {
        if (!this._instantiated) {
            this._instantiated = true;
            await this._ldClient.waitUntilReady();
            await this.changeUser(this.identityService.current);
            this.loadFlags();
            this._ldClient.on('change', (flags) => {
                this.updateFlags(flags);
            });
        }
    }

    public isEnabled(featureName: FeatureFlag): Observable<boolean> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => typeof flags.flagsState[featureName] === 'boolean'),
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => flags.flagsState[featureName])
        );
    }

    isEnabledSignal(featureName: FeatureFlag, initialValue = false): Signal<boolean> {
        return toSignal(this.isEnabled(featureName), { initialValue });
    }

    public isDisabled(featureName: FeatureFlag): Observable<boolean> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => typeof flags.flagsState[featureName] === 'boolean'),
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => !flags.flagsState[featureName])
        );
    }

    public getNumberFeatureFlag(featureName: FeatureFlag): Observable<number> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => flags.flagsState[featureName])
        );
    }

    public getJsonFeatureFlag<T>(featureName: FeatureFlag): Observable<T> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => flags.flagsState[featureName])
        );
    }

    public selectFeatureFlag(featureName: FeatureFlag): Observable<FeatureManagementFlag> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => ({
                enabled: flags.flagsState[featureName] === true,
                value: flags.flagsState[featureName],
                flagName: featureName
            }))
        );
    }

    public getUser(): LDClient.LDContext {
        return this._ldClient.getContext();
    }

    public changeUser(identityUser?: Identity): Promise<LDClient.LDFlagSet> {
        const user = this.createUser(identityUser);
        return this._ldClient.identify(user);
    }

    public dispose(): Observable<any> {
        return from(this._ldClient.close());
    }

    private loadFlags(): void {
        this._cachedFlags = this._ldClient.allFlags();
        const allFlagNames = Object.values(FeatureFlag);
        this._flags$.next({ flagsState: this._cachedFlags, changedFlags: allFlagNames });
    }

    private updateFlags(flags: LDClient.LDFlagChangeset): void {
        if (this._cachedFlags !== undefined && this._cachedFlags !== null) {
            const updatedFlags = [];
            Object.values(FeatureFlag).forEach((flagName) => {
                if (flags[flagName] !== undefined) {
                    this._cachedFlags[flagName] = flags[flagName].current;
                    if (flags[flagName].current !== flags[flagName].previous) {
                        updatedFlags.push(flagName);
                    }
                }
            });
            if (updatedFlags.length !== 0) {
                this._flags$.next({ flagsState: this._cachedFlags, changedFlags: updatedFlags });
            }
        }
    }

    private createUser(identityUser: Identity): LDClient.LDContext {
        if (identityUser?.userId) {
            const user = {
                key: identityUser.userId,
                name: identityUser.userName,
                email: identityUser.email,
                firstName: identityUser.firstName,
                lastName: identityUser.lastName,
                anonymous: false
            };
            return user;
        } else {
            return { key: 'anonymous', anonymous: true };
        }
    }

    /**
     * @summary Checks if Nova Physical Units feature is enabled for a specific facility.
     * This method first verifies if the child feature flag is enabled in the current context,
     * and only if it is, proceeds to check the parent flag in the facility context.
     * This approach is safe because:
     * 1. We check the child flag first to avoid unnecessary context switching
     * 2. The facility context is only changed if the child flag is enabled
     * 3. The context is always reset back to the user context in the finalize operator
     * 4. Both flags must be enabled for the feature to be considered active
     * @param facilityId - The ID of the facility to check the feature for
     * @param childFlag - The child feature flag to check
     * @returns Observable<boolean> - true if both parent and child flags are enabled
     */
    public isNovaPhysicalUnitsEnabled(facilityId: string, childFlag: FeatureFlag): Observable<boolean> {
        return this.isEnabled(childFlag).pipe(
            switchMap((isChildFlagEnabled) =>
                from(this.changeFacilityContext(facilityId)).pipe(
                    switchMap(() => this.isEnabled(FeatureFlag.UnitVersePhysicalUnitParent)),
                    map((parentFlagEnabled) => parentFlagEnabled && isChildFlagEnabled),
                    finalize(() => this.resetToUserContext())
                )
            )
        );
    }

    private resetToUserContext(): Promise<LDClient.LDFlagSet> {
        return this.changeUser(this.identityService.current);
    }

    /**
     * @summary Changes the facility context in LaunchDarkly.
     * This method switches the context to a specific facility for flag evaluation.
     * @param facilityId - The ID of the facility to switch to
     * @returns Promise<LDClient.LDFlagSet> - The updated flag set after context change
     */
    public changeFacilityContext(facilityId: string): Promise<LDClient.LDFlagSet> {
        const context = this.createFacilityContext(facilityId);
        return this._ldClient.identify(context);
    }

    private createFacilityContext(facilityId: string): LDClient.LDContext {
        return {
            kind: 'facility',
            key: facilityId,
            attributes: {
                facilityId: facilityId
            }
        };
    }
}

interface FlagsState {
    flagsState: LDClient.LDFlagSet;
    changedFlags: string[];
}
